<template>
  <div class="ceo-news-root">
    <!-- ── Top Header Bar ─────────────────────────────────────────────── -->
    <header class="ceo-topbar">
      <div class="brand" @click="$router.push('/ceo-dashboard')" style="cursor: pointer;">
        <div class="brand-logo-box"><img :src="wtiLogo" class="brand-logo" alt="WTI" /></div>
        <div class="brand-text">
          <div class="brand-title">External Media News</div>
          <div class="brand-sub">Wildlife Trust of India · Third-Party Coverage</div>
        </div>
      </div>

      <div class="topbar-actions">
        <button class="btn ghost" @click="$router.push('/ceo-dashboard')">
          <v-icon size="17">mdi-arrow-left</v-icon><span>CEO Overview</span>
        </button>
        <button class="btn ghost" :disabled="scraping" @click="triggerScraper">
          <v-icon size="17" :class="{ spin: scraping }">mdi-spider-web</v-icon>
          <span>{{ scraping ? 'Scanning Media…' : 'Run Live Scraper' }}</span>
        </button>
        <button class="btn solid" @click="openAddDialog">
          <v-icon size="17">mdi-plus-circle-outline</v-icon><span>Add News Link</span>
        </button>
      </div>
    </header>

    <div v-if="loading" class="loader-wrap">
      <v-progress-circular indeterminate color="#14663a" size="52" width="4" />
      <div class="loading-text">Loading External Media News…</div>
    </div>

    <main v-else class="news-body">
      <!-- ═══ SENTIMENT & COVERAGE SUMMARY ═══════════════════════════════ -->
      <section class="summary-grid">
        <div class="sum-card total">
          <div class="sc-head"><v-icon color="#14663a" size="20">mdi-newspaper-variant-multiple-outline</v-icon> Total Media Coverage</div>
          <div class="sc-val">{{ stats.total }}</div>
          <div class="sc-sub">Articles tracked across external media</div>
        </div>

        <div class="sum-card positive">
          <div class="sc-head"><v-icon color="#2e7d32" size="20">mdi-emoticon-happy-outline</v-icon> Positive Mentions</div>
          <div class="sc-val pos">{{ stats.positive }} <span class="sc-pct">({{ stats.positivePct }}%)</span></div>
          <div class="sc-sub">Conservation successes &amp; community wins</div>
        </div>

        <div class="sum-card negative">
          <div class="sc-head"><v-icon color="#c62828" size="20">mdi-emoticon-sad-outline</v-icon> Negative / Alert Mentions</div>
          <div class="sc-val neg">{{ stats.negative }}</div>
          <div class="sc-sub">Poaching alerts, delays &amp; critical issues</div>
        </div>

        <div class="sum-card neutral">
          <div class="sc-head"><v-icon color="#475569" size="20">mdi-information-outline</v-icon> Neutral / Informational</div>
          <div class="sc-val neu">{{ stats.neutral }}</div>
          <div class="sc-sub">General press releases &amp; notices</div>
        </div>
      </section>

      <!-- ═══ FILTER BAR ══════════════════════════════════════════════════ -->
      <section class="filter-panel">
        <div class="fp-left">
          <div class="search-box">
            <v-icon size="18" color="#64748b">mdi-magnify</v-icon>
            <input type="text" v-model="searchQuery" placeholder="Search by headline, publisher, or author…" @input="debouncedFetch" />
            <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''; fetchNews()">
              <v-icon size="14">mdi-close</v-icon>
            </button>
          </div>

          <div class="chip-group">
            <button class="filter-chip" :class="{ active: dateFilter === 'yesterday' }" @click="setDateFilter('yesterday')">Starting Yesterday</button>
            <button class="filter-chip" :class="{ active: dateFilter === '7days' }" @click="setDateFilter('7days')">Last 7 Days</button>
            <button class="filter-chip" :class="{ active: dateFilter === '30days' }" @click="setDateFilter('30days')">Last 30 Days</button>
            <button class="filter-chip" :class="{ active: dateFilter === 'all' }" @click="setDateFilter('all')">All Time</button>
          </div>
        </div>

        <div class="fp-right">
          <span class="fp-label">Sentiment:</span>
          <div class="chip-group">
            <button class="filter-chip" :class="{ active: selectedSentiment === 'all' }" @click="setSentimentFilter('all')">All</button>
            <button class="filter-chip pos" :class="{ active: selectedSentiment === 'positive' }" @click="setSentimentFilter('positive')">
              <v-icon size="14" color="#2e7d32">mdi-check-circle</v-icon> Positive
            </button>
            <button class="filter-chip neg" :class="{ active: selectedSentiment === 'negative' }" @click="setSentimentFilter('negative')">
              <v-icon size="14" color="#c62828">mdi-alert-circle</v-icon> Negative
            </button>
            <button class="filter-chip neu" :class="{ active: selectedSentiment === 'neutral' }" @click="setSentimentFilter('neutral')">
              <v-icon size="14" color="#475569">mdi-minus-circle</v-icon> Neutral
            </button>
          </div>
        </div>
      </section>

      <!-- ═══ ARTICLES LIST / GRID ════════════════════════════════════════ -->
      <section v-if="items.length" class="articles-grid">
        <div v-for="item in items" :key="item._id" class="article-card" :class="item.sentiment">
          <div class="ac-header">
            <div class="ac-pub-info">
              <span class="pub-name"><v-icon size="15" color="#14663a">mdi-newspaper</v-icon> {{ item.publisher || 'External Media' }}</span>
              <span class="pub-author" v-if="item.author">· {{ item.author }}</span>
            </div>
            <span class="sentiment-badge" :class="item.sentiment">
              <v-icon size="13">{{ sentimentIcon(item.sentiment) }}</v-icon>
              {{ item.sentiment.toUpperCase() }}
            </span>
          </div>

          <h3 class="ac-title">
            <a :href="item.articleUrl" target="_blank" rel="noopener noreferrer">
              {{ item.title }}
              <v-icon size="14" color="#14663a" class="ext-ic">mdi-open-in-new</v-icon>
            </a>
          </h3>

          <p class="ac-snippet">{{ item.snippet }}</p>

          <div class="ac-footer">
            <div class="ac-date">
              <v-icon size="14" color="#8aa89a">mdi-clock-outline</v-icon>
              {{ formatDate(item.publishedAt) }}
              <span class="source-tag" v-if="item.source">({{ item.source }})</span>
            </div>

            <div class="ac-actions">
              <!-- Sentiment toggle dropdown menu -->
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <button class="act-btn" v-bind="attrs" v-on="on" title="Adjust Sentiment">
                    <v-icon size="15" color="#475569">mdi-tag-outline</v-icon> Edit Sentiment
                  </button>
                </template>
                <v-list dense class="sent-menu">
                  <v-list-item @click="updateSentiment(item, 'positive')">
                    <v-list-item-icon><v-icon color="#2e7d32" size="16">mdi-emoticon-happy-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Mark Positive</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="updateSentiment(item, 'negative')">
                    <v-list-item-icon><v-icon color="#c62828" size="16">mdi-emoticon-sad-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Mark Negative</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="updateSentiment(item, 'neutral')">
                    <v-list-item-icon><v-icon color="#475569" size="16">mdi-minus-circle-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Mark Neutral</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <a :href="item.articleUrl" target="_blank" rel="noopener noreferrer" class="read-btn">
                Read Full Article <v-icon size="14">mdi-arrow-right</v-icon>
              </a>
            </div>
          </div>
        </div>
      </section>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <v-icon size="48" color="#a7b3ac">mdi-newspaper-remove</v-icon>
        <h3>No media news articles found</h3>
        <p>Try adjusting your search query, sentiment filter, or date range.</p>
        <button class="btn solid" style="margin-top: 12px;" @click="resetFilters">Reset Filters</button>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="pagination.totalPages > 1">
        <button class="btn ghost" :disabled="pagination.page <= 1" @click="changePage(pagination.page - 1)">
          <v-icon size="16">mdi-chevron-left</v-icon> Previous
        </button>
        <span class="page-info">Page {{ pagination.page }} of {{ pagination.totalPages }}</span>
        <button class="btn ghost" :disabled="pagination.page >= pagination.totalPages" @click="changePage(pagination.page + 1)">
          Next <v-icon size="16">mdi-chevron-right</v-icon>
        </button>
      </div>
    </main>

    <!-- ═══ ADD NEWS DIALOG ═══════════════════════════════════════════════ -->
    <v-dialog v-model="addDialog" max-width="560px" persistent>
      <v-card class="add-card">
        <v-card-title class="add-title">
          <span>Add External Media Article</span>
          <v-btn icon @click="addDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="add-body">
          <v-text-field v-model="newArticle.title" label="Article Title / Headline *" outlined dense class="mb-2" />
          <v-text-field v-model="newArticle.articleUrl" label="Article Link (URL) *" outlined dense class="mb-2" />
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="newArticle.publisher" label="Channel / Paper / Website" outlined dense placeholder="e.g. The Hindu" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="newArticle.author" label="Author / Reporter" outlined dense placeholder="e.g. Staff Reporter" />
            </v-col>
          </v-row>
          <v-select v-model="newArticle.sentiment" :items="sentimentOptions" label="Sentiment" outlined dense class="mb-2" />
          <v-textarea v-model="newArticle.snippet" label="Snippet / Brief Summary" outlined dense rows="3" />
        </v-card-text>
        <v-card-actions class="add-actions">
          <v-btn text @click="addDialog = false">Cancel</v-btn>
          <v-btn color="#14663a" class="white--text" :loading="savingArticle" @click="submitAddArticle">Save News Link</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackColor" timeout="3500">
      {{ snackMsg }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'CeoMediaNews',
  data() {
    return {
      wtiLogo: require('@/assets/images/wtilogo.png'),

      loading: true,
      scraping: false,
      savingArticle: false,
      addDialog: false,
      searchQuery: '',
      dateFilter: 'yesterday',
      selectedSentiment: 'all',
      items: [],
      stats: { total: 0, positive: 0, negative: 0, neutral: 0, positivePct: 0 },
      pagination: { page: 1, limit: 12, totalItems: 0, totalPages: 1 },
      newArticle: { title: '', articleUrl: '', publisher: '', author: '', sentiment: 'positive', snippet: '' },
      sentimentOptions: [
        { text: 'Positive (Green)', value: 'positive' },
        { text: 'Negative (Red)', value: 'negative' },
        { text: 'Neutral (Slate)', value: 'neutral' }
      ],
      snackbar: false,
      snackMsg: '',
      snackColor: '#14663a',
      debounceTimer: null
    };
  },
  mounted() {
    this.fetchNews();
  },
  methods: {
    authHeaders() {
      const token = localStorage.getItem('token') || this.$store.state.token;
      return { Authorization: `Bearer ${token}` };
    },
    async fetchNews() {
      this.loading = true;
      try {
        const res = await axios.get('dashboard/ceo/media-news', {
          params: {
            sentiment: this.selectedSentiment,
            search: this.searchQuery,
            dateFilter: this.dateFilter,
            page: this.pagination.page,
            limit: this.pagination.limit
          },
          headers: this.authHeaders()
        });

        if (res.data && res.data.status) {
          this.items = res.data.items || [];
          this.stats = res.data.stats || { total: 0, positive: 0, negative: 0, neutral: 0, positivePct: 0 };
          this.pagination = res.data.pagination || { page: 1, limit: 12, totalItems: 0, totalPages: 1 };
        }
      } catch (err) {
        console.error('Error fetching media news:', err);
        this.showSnack('Failed to load media news', '#c62828');
      } finally {
        this.loading = false;
      }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchNews();
      }, 400);
    },
    setDateFilter(val) {
      this.dateFilter = val;
      this.pagination.page = 1;
      this.fetchNews();
    },
    setSentimentFilter(val) {
      this.selectedSentiment = val;
      this.pagination.page = 1;
      this.fetchNews();
    },
    changePage(p) {
      this.pagination.page = p;
      this.fetchNews();
    },
    resetFilters() {
      this.searchQuery = '';
      this.dateFilter = 'all';
      this.selectedSentiment = 'all';
      this.pagination.page = 1;
      this.fetchNews();
    },
    async triggerScraper() {
      this.scraping = true;
      try {
        const res = await axios.post('dashboard/ceo/media-news/scrape', {}, { headers: this.authHeaders() });
        if (res.data && res.data.status) {
          this.showSnack(res.data.msg || 'Media news refresh complete', '#14663a');
          this.fetchNews();
        } else {
          this.showSnack(res.data.msg || 'Failed to refresh media news', '#c62828');
        }
      } catch (err) {
        console.error('Error running scraper:', err);
        this.showSnack('Error triggering media scraper', '#c62828');
      } finally {
        this.scraping = false;
      }
    },
    async updateSentiment(item, newSentiment) {
      try {
        const res = await axios.post('dashboard/ceo/media-news/sentiment', {
          articleId: item._id,
          sentiment: newSentiment
        }, { headers: this.authHeaders() });

        if (res.data && res.data.status) {
          item.sentiment = newSentiment;
          this.showSnack(`Sentiment updated to ${newSentiment}`, '#14663a');
          this.fetchNews();
        }
      } catch (err) {
        console.error('Error updating sentiment:', err);
        this.showSnack('Failed to update sentiment', '#c62828');
      }
    },
    openAddDialog() {
      this.newArticle = { title: '', articleUrl: '', publisher: '', author: '', sentiment: 'positive', snippet: '' };
      this.addDialog = true;
    },
    async submitAddArticle() {
      if (!this.newArticle.title || !this.newArticle.articleUrl) {
        this.showSnack('Title and Article Link are required', '#c62828');
        return;
      }

      this.savingArticle = true;
      try {
        const res = await axios.post('dashboard/ceo/media-news/add', this.newArticle, { headers: this.authHeaders() });
        if (res.data && res.data.status) {
          this.showSnack('Article added successfully', '#14663a');
          this.addDialog = false;
          this.fetchNews();
        } else {
          this.showSnack(res.data.msg || 'Failed to add article', '#c62828');
        }
      } catch (err) {
        console.error('Error adding article:', err);
        this.showSnack('Error saving media article', '#c62828');
      } finally {
        this.savingArticle = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return 'Recent';
      const m = moment(dateStr);
      if (m.isSame(moment(), 'day')) {
        return 'Today at ' + m.format('h:mm A');
      }
      if (m.isSame(moment().subtract(1, 'day'), 'day')) {
        return 'Yesterday at ' + m.format('h:mm A');
      }
      return m.format('MMM D, YYYY · h:mm A');
    },
    sentimentIcon(s) {
      if (s === 'positive') return 'mdi-emoticon-happy';
      if (s === 'negative') return 'mdi-emoticon-sad';
      return 'mdi-minus-circle';
    },
    showSnack(msg, color = '#14663a') {
      this.snackMsg = msg;
      this.snackColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.ceo-news-root {
  min-height: 100vh;
  background: #09120d;
  color: #e2e8f0;
  font-family: 'Kumbh Sans', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding-bottom: 48px;
}

/* ── Header ───────────────────────────────────────────────────────── */
.ceo-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  background: #0f2317;
  border-bottom: 1px solid #1a3d29;
  position: sticky;
  top: 0;
  z-index: 99;
}
.brand {
  display: flex;
  align-items: center;
  gap: 14px;
}
.brand-logo-box {
  width: 42px;
  height: 42px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}
.brand-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.brand-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #f8fafc;
  letter-spacing: -0.01em;
}
.brand-sub {
  font-size: 0.8rem;
  color: #8aa89a;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}
.btn.ghost {
  background: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.btn.ghost:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
.btn.solid {
  background: #14663a;
  color: #fff;
}
.btn.solid:hover {
  background: #1a7a46;
}

.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  100% { transform: rotate(360deg); }
}

/* ── Loader ────────────────────────────────────────────────────────── */
.loader-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
}
.loading-text {
  color: #8aa89a;
  font-size: 0.95rem;
}

/* ── Main body ─────────────────────────────────────────────────────── */
.news-body {
  max-width: 1320px;
  margin: 24px auto;
  padding: 0 24px;
}

/* ── Summary grid ─────────────────────────────────────────────────── */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}
.sum-card {
  background: #122218;
  border: 1px solid #1e3a29;
  border-radius: 10px;
  padding: 18px 20px;
}
.sc-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 8px;
}
.sc-val {
  font-size: 1.8rem;
  font-weight: 800;
  color: #f8fafc;
  line-height: 1.2;
}
.sc-val.pos { color: #4ade80; }
.sc-val.neg { color: #f87171; }
.sc-val.neu { color: #94a3b8; }
.sc-pct {
  font-size: 1rem;
  font-weight: 600;
}
.sc-sub {
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 4px;
}

/* ── Filter panel ─────────────────────────────────────────────────── */
.filter-panel {
  background: #122218;
  border: 1px solid #1e3a29;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}
.fp-left, .fp-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #09120d;
  border: 1px solid #234731;
  border-radius: 6px;
  padding: 6px 12px;
  min-width: 280px;
}
.search-box input {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 0.85rem;
  outline: none;
  width: 100%;
}
.clear-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}
.fp-label {
  font-size: 0.82rem;
  color: #8aa89a;
  font-weight: 600;
}
.chip-group {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.filter-chip {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.filter-chip:hover {
  background: rgba(255, 255, 255, 0.12);
}
.filter-chip.active {
  background: #14663a;
  border-color: #1a7a46;
  color: #fff;
}

/* ── Articles Grid ────────────────────────────────────────────────── */
.articles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}
.article-card {
  background: #112016;
  border: 1px solid #1a3927;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: transform 0.2s ease, border-color 0.2s ease;
}
.article-card:hover {
  transform: translateY(-2px);
  border-color: #26553a;
}
.article-card.positive {
  border-left: 4px solid #2e7d32;
}
.article-card.negative {
  border-left: 4px solid #c62828;
}
.article-card.neutral {
  border-left: 4px solid #475569;
}

.ac-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.ac-pub-info {
  font-size: 0.8rem;
  color: #94a3b8;
}
.pub-name {
  color: #a7f3d0;
  font-weight: 700;
}
.sentiment-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.03em;
}
.sentiment-badge.positive {
  background: rgba(46, 125, 50, 0.2);
  color: #4ade80;
  border: 1px solid rgba(74, 222, 128, 0.3);
}
.sentiment-badge.negative {
  background: rgba(198, 40, 40, 0.2);
  color: #f87171;
  border: 1px solid rgba(248, 113, 113, 0.3);
}
.sentiment-badge.neutral {
  background: rgba(71, 85, 105, 0.2);
  color: #cbd5e1;
  border: 1px solid rgba(203, 213, 225, 0.3);
}

.ac-title {
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.4;
  margin: 0 0 10px 0;
}
.ac-title a {
  color: #f8fafc;
  text-decoration: none;
  transition: color 0.2s ease;
}
.ac-title a:hover {
  color: #34d399;
}
.ext-ic {
  margin-left: 4px;
  vertical-align: middle;
}

.ac-snippet {
  font-size: 0.84rem;
  color: #94a3b8;
  line-height: 1.5;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ac-footer {
  border-top: 1px solid #1a3927;
  padding-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ac-date {
  font-size: 0.76rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
}
.source-tag {
  color: #475569;
}

.ac-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.act-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.74rem;
  cursor: pointer;
}
.act-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
.read-btn {
  font-size: 0.78rem;
  font-weight: 700;
  color: #34d399;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.read-btn:hover {
  text-decoration: underline;
}

/* ── Empty state & Pagination ──────────────────────────────────────── */
.empty-state {
  text-align: center;
  padding: 48px 20px;
  background: #112016;
  border: 1px dashed #1a3927;
  border-radius: 12px;
}
.empty-state h3 {
  margin: 12px 0 6px 0;
  color: #e2e8f0;
}
.empty-state p {
  color: #64748b;
  font-size: 0.88rem;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
}
.page-info {
  font-size: 0.85rem;
  color: #8aa89a;
}

/* ── Add modal ─────────────────────────────────────────────────────── */
.add-card {
  background: #0f2317 !important;
  color: #fff !important;
}
.add-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #1a3d29;
  font-size: 1.1rem;
  font-weight: 700;
}
.add-body {
  padding-top: 16px !important;
}
.add-actions {
  border-top: 1px solid #1a3d29;
  justify-content: flex-end;
}
.sent-menu {
  background: #0f2317 !important;
  color: #fff !important;
}
</style>
